<template>
    <div>
        <template v-if="dataLoaded && survey">
            <h3>Editar Encuesta/Quiz:</h3>
            <h2 class="mb-4 text-center"><strong>{{ survey.name }}</strong></h2>
            <h4>
                Detalles
                <b-button
                    v-if="displayUpdateButton"
                    class="ml-2"
                    variant="warning"
                    @click="onUpdate()"
                    v-b-tooltip.hover
                    title="Editar Encuesta/Quiz"
                >
                    <b-icon-pencil />
                </b-button>
                <b-button
                    v-if="isUserStudenTeacherOrBetter"
                    class="ml-2"
                    variant="primary"
                    @click="onClone()"
                    v-b-tooltip.hover
                    title="Clonar Encuesta/Quiz"
                >
                    <b-icon-files /> Clonar
                </b-button>
                <b-button
                    v-if="displayOpenSurveyButton"
                    variant="success"
                    class="ml-2 px-4"
                    :disabled="!survey.valid || survey.studentSurveyCount < 1"
                    @click="onOpenSurvey()"
                >
                    <b-icon-door-open-fill /> Abrir
                </b-button>
                <b-button
                    v-if="!displayOpenSurveyButton"
                    variant="dark"
                    class="ml-2 px-3"
                    @click="onCloseSurvey()"
                >
                    <b-icon-door-closed-fill /> Cerrar
                </b-button>
                <b-button
                    v-if="isUserStudenTeacherOrBetter"
                    class="ml-2"
                    variant="danger"
                    @click="onDelete()"
                    v-b-tooltip.hover
                    title="Eliminar Encuesta/Quiz"
                >
                    <b-icon-trash-fill />
                </b-button>
            </h4>
            <hr />
            <!-- Survey details -->
            <b-container v-if="dataLoaded" fluid>
                <b-row>
                    <b-col sm="6">
                        <p class="tall-line-height">
                            <strong>Tipo: </strong>{{ survey.isEvaluatedDisplay }}<br />
                            <strong>Estado: </strong>
                            <span style="font-size: 1.1rem;">
                                <span v-if="survey.state === 'draft'">
                                    <b-badge variant="info" class="py-2 px-3">Borrador</b-badge>
                                </span>
                                <span v-else-if="survey.state === 'open'">
                                    <b-badge variant="success" class="py-2 px-3">Abierto</b-badge>
                                </span>
                                <span v-else-if="survey.state === 'closed'">
                                    <b-badge variant="dark" class="py-2 px-3">Cerrado</b-badge>
                                </span>
                            </span>
                        </p>
                    </b-col>
                    <b-col sm="6">
                        <p class="tall-line-height">
                            <strong>Duración Máxima: </strong>
                            <span v-if="survey.durationMinutes > 0">{{ survey.durationMinutes }} minutos.<br /></span>
                            <span v-else>Indefinida.<br /></span>
                            <strong>Desplegar Respuestas: </strong>{{ survey.showAnswersDisplay }}
                        </p>
                    </b-col>
                </b-row>
                <b-row class="mb-4">
                    <b-col cols="12">
                        <p class="display-breaks text-justify">
                            <strong>Descripción: </strong>
                            <br /><span v-if="survey.description">{{ survey.description }}</span>
                            <span v-else ><em>No hay descripción displonible.</em></span>
                        </p>
                    </b-col>
                </b-row>
            </b-container>

            <b-card no-body>
                <b-tabs active-nav-item-class="bg-primary text-white" justified card>
                    <b-tab title="Preguntas" :title-link-class="bTabLinkClasses" active>
                        <div
                            v-if="canEditSurvey || survey.isEvaluated"
                            class="my-2"
                            style="min-height: 30px; font-size: 1rem;"
                        >
                            <span v-if="survey.isEvaluated">
                                Ponderación Total:
                                <strong :class="{'text-danger': survey.valueTotal !== '100.00'}">
                                    {{ survey.valueTotal }} %
                                </strong>
                            </span>
                            <b-button
                                v-if="isUserStudenTeacherOrBetter && canEditSurvey"
                                class="ml-2 float-right"
                                variant="primary"
                                @click="onCreateQuestion()"
                            >
                                <b-icon-plus /> Agregar
                            </b-button>
                        </div>
                        <h5
                            v-if="displayValueTotalError"
                            class="ml-4 mb-4 text-danger"
                        >
                            <em>
                                La ponderación total del quiz debe ser igual a 100.00 %,
                                <span v-if="Number(remainingValueTotal) > 0">falta {{ remainingValueTotal }} %.</span>
                                <span v-else>sobra {{ (-Number(remainingValueTotal)).toFixed(2) }} %.</span>
                            </em>
                        </h5>

                        <!-- Survey questions and choices -->
                        <template v-if="survey.questions.length">
                            <SurveyQuestion
                                v-for="(question, index) in survey.questions"
                                :key="`question-${question.id}`"
                                :question="question"
                                :number="index + 1"
                                @move-question-up="switchQuestionOrders"
                                @move-question-down="switchQuestionOrders"
                            />
                        </template>
                        <h4 v-else class="text-danger"><em>No hay preguntas en esta encuesta/quiz.</em></h4>
                    </b-tab>
                    <b-tab title="Participantes" :title-link-class="bTabLinkClasses">
                        <SurveyParticipants v-if="survey.valid"/>
                        <h4 v-else class="text-danger text-center my-3">
                            Para agregar participantes por favor resuelva los errores presentes en la sección de Preguntas.
                        </h4>
                    </b-tab>
                    <b-tab title="Respuestas" :title-link-class="bTabLinkClasses">
                        <SurveyAnswers v-if="survey.studentSurveyCount > 0 && survey.state !== 'draft'"/>
                        <h4 v-else class="text-danger text-center my-3">
                            Para ver respuestas primero debe agregar participantes y abrir la encuesta/quiz.
                        </h4>
                    </b-tab>
                </b-tabs>
            </b-card>
        </template>

        <!-- Update Survey Modal -->
        <SurveyCreateUpdateModal v-if="isUserStudenTeacherOrBetter" />

        <!-- Create/Update Survey Question Modal -->
        <QuestionCreateUpdateModal v-if="isUserStudenTeacherOrBetter" />

        <!-- Create/Update Survey Question Choices Modal -->
        <ChoiceCreateUpdateModal v-if="isUserStudenTeacherOrBetter" />

        <!-- Clone Survey Modal -->
        <SurveyCloneModal v-if="isUserStudenTeacherOrBetter" />

    </div>
</template>

<script>
import SurveyCreateUpdateModal from './components/SurveyCreateUpdateModal'
import SurveyQuestion from './components/SurveyQuestion'
import SurveyParticipants from './components/SurveyParticipants'
import SurveyAnswers from './components/SurveyAnswers'
import QuestionCreateUpdateModal from './components/QuestionCreateUpdateModal'
import ChoiceCreateUpdateModal from './components/ChoiceCreateUpdateModal'
import SurveyCloneModal from './components/SurveyCloneModal'

import { mapState, mapGetters, mapActions } from 'vuex'

export default {
    name: 'TeachingStaffSurveyDetail',
    components: {
        SurveyCreateUpdateModal,
        SurveyQuestion,
        SurveyParticipants,
        SurveyAnswers,
        QuestionCreateUpdateModal,
        ChoiceCreateUpdateModal,
        SurveyCloneModal
    },
    data () {
        return {
            bTabLinkClasses: ['py-3'],
            dataLoaded: false
        }
    },
    computed: {
        ...mapState({
            activeSite: 'activeSite',
            selectedCrudObject: 'selectedCrudObject',
            survey: 'selectedSurveyObject'
        }),
        ...mapGetters([
            'isUserStudenTeacherOrBetter',
            'canEditSurvey'
        ]),
        remainingValueTotal () {
            if (this.survey) {
                var total = Number(this.survey.valueTotal)
                return (100 - total).toFixed(2)
            }
            return null
        },
        displayUpdateButton () {
            return (
                this.isUserStudenTeacherOrBetter &&
                this.survey &&
                (this.survey.state === 'draft' || this.survey.isEvaluated)
            )
        },
        displayOpenSurveyButton () {
            return this.survey.state !== 'open'
        },
        displayValueTotalError () {
            return (
                this.survey.valueTotal !== '100.00' &&
                this.survey.questions.length &&
                this.survey.isEvaluated
            )
        }
    },
    watch: {
        async '$route' (to, from) {
            // Only reload site from backend API if the url surveyId parameter changed
            if (to.params.surveyId && from.params.surveyId) {
                if (to.params.surveyId !== from.params.surveyId) {
                    await this.loadwholeSurveyData()
                }
            }
        }
    },
    methods: {
        ...mapActions([
            'readSingleSiteTeacherSurvey',
            'switchSurveyQuestionOrders',
            'openSiteSurvey',
            'closeSiteSurvey',
            'deleteSiteSurvey'
        ]),
        async loadwholeSurveyData () {
            let loader = this.$loading.show()
            this.dataLoaded = false
            try {
                if (this.isUserStudenTeacherOrBetter) {
                    var payload = { courseSiteId: this.activeSite.id, surveyId: this.$route.params.surveyId }
                    await this.readSingleSiteTeacherSurvey(payload)
                }
                this.dataLoaded = true
            } catch (err) {
                console.log(err)
                if (err.response && (err.response.status === 404 || err.response.status === 403)) {
                    // If the survey does not exists or the user is not part of the teaching staff
                    // Redirect the user to the site general info section
                    this.$router.replace({ name: 'site-general-info' })
                } else {
                    this.$root.unknownErrorToast()
                }
                this.dataLoaded = false
            } finally {
                loader.hide()
            }
        },
        onCreateQuestion () {
            this.$store.commit('SET_SELECTED_CRUD_OBJECT', null)
            this.$bvModal.show('modal-create-survey-question')
        },
        async switchQuestionOrders (questionIndex1, questionIndex2) {
            console.log('switching questions', { questionIndex1, questionIndex2 })
            var questionId1 = this.survey.questions[questionIndex1].id
            var questionId2 = this.survey.questions[questionIndex2].id
            if (questionId1 === undefined || questionId2 === undefined) {
                // Abort and pop out error in toast
                this.$root.dangerToast('No se pudo cambiar el orden de la pregunta, recargue la página o intente más tarde.')
            } else {
                var loader = this.$loading.show()
                const payloadData = {
                    courseSiteId: this.activeSite.id,
                    surveyId: this.survey.id,
                    questionId1,
                    questionId2
                }
                var reloadPayload = { courseSiteId: this.activeSite.id, surveyId: this.survey.id }
                try {
                    await this.switchSurveyQuestionOrders(payloadData)
                    await this.readSingleSiteTeacherSurvey(reloadPayload)
                } catch (error) {
                    console.log({ error: error })
                    this.$root.dangerToast('No se pudo cambiar el orden de la pregunta, recargue la página o intente más tarde.')
                } finally {
                    loader.hide()
                }
            }
        },
        onClone () {
            this.$store.commit('SET_SELECTED_CRUD_OBJECT', this.survey)
            this.$bvModal.show('modal-clone-survey')
        },
        onUpdate () {
            this.$store.commit('SET_SELECTED_CRUD_OBJECT', this.survey)
            this.$bvModal.show('modal-create-survey')
        },
        async onOpenSurvey () {
            let nodesList = [
                '¿Esta seguro que desea abrir la encuesta/quiz? ' +
                'Esto permitirá que los participantes respondan las preguntas de la misma, a menos ' +
                'que la hayan completado previamente.',
                this.$createElement('br'),
                this.$createElement('br')
            ]
            if (this.survey.state === 'draft') {
                nodesList.push(this.$createElement('strong', ['La encuesta/quiz NO será editable una vez sea abierta.']))
            }
            let modalBodyText = this.$createElement('p', nodesList)
            try {
                let value = await this.$bvModal.msgBoxConfirm([modalBodyText], {
                    title: '¡Atención!',
                    okVariant: 'success',
                    okTitle: 'Abrir',
                    cancelTitle: 'Cancelar',
                    centered: true
                })
                if (value) {
                    // Open the survey/quiz after confirmation
                    var loader = this.$loading.show()
                    try {
                        const payloadData = { courseSiteId: this.activeSite.id, surveyId: this.survey.id }
                        await this.openSiteSurvey(payloadData)
                        const reloadPayload = { courseSiteId: this.activeSite.id, surveyId: this.survey.id }
                        await this.readSingleSiteTeacherSurvey(reloadPayload)
                        this.$root.successToast(`La encuesta/quiz se ha abierto a los participantes.`)
                    } catch (error) {
                        console.log({ error: error })
                        this.$root.dangerToast('No se pudo abrir la encuesta/quiz, intente más tarde.')
                    } finally {
                        loader.hide()
                    }
                }
            } catch (error) {
                console.log({ error: error })
                this.unknownErrorToast()
            }
        },
        async onCloseSurvey () {
            let modalBodyText = this.$createElement('p', [
                '¿Esta seguro que desea cerrar la encuesta/quiz?',
                this.$createElement('br'),
                this.$createElement('br'),
                'Esto completará automáticamente todas las encuestas/quices de los participantes ' +
                'evitando que los participantes respondan más preguntas de la misma.'
            ])
            try {
                let value = await this.$bvModal.msgBoxConfirm([modalBodyText], {
                    title: '¡Atención!',
                    okVariant: 'dark',
                    okTitle: 'Cerrar',
                    cancelTitle: 'Cancelar',
                    centered: true
                })
                if (value) {
                    // Close the survey/quiz after confirmation
                    var loader = this.$loading.show()
                    try {
                        const payloadData = { courseSiteId: this.activeSite.id, surveyId: this.survey.id }
                        await this.closeSiteSurvey(payloadData)
                        const reloadPayload = { courseSiteId: this.activeSite.id, surveyId: this.survey.id }
                        await this.readSingleSiteTeacherSurvey(reloadPayload)
                        this.$root.successToast(`La encuesta/quiz se ha cerrado.`)
                    } catch (error) {
                        console.log({ error: error })
                        this.$root.dangerToast('No se pudo cerrar la encuesta/quiz, intente más tarde.')
                    } finally {
                        loader.hide()
                    }
                }
            } catch (error) {
                console.log({ error: error })
                this.unknownErrorToast()
            }
        },
        async onDelete () {
            this.$store.commit('SET_SELECTED_CRUD_OBJECT', this.survey)
            var modalBodyText = this.$createElement('p', [
                '¿Esta seguro que desea eliminar esta encuesta/quiz? ' +
                'Esto también eliminará todas las preguntas, opciones y ' +
                'respuestas de los estudiantes relacionadas.',
                this.$createElement('br'),
                this.$createElement('br'),
                'Si en cambio lo que desea es evitar que los estudiantes puedan ver ' +
                'esta encuesta/quiz, puede cerrarla.'
            ])
            try {
                let value = await this.$bvModal.msgBoxConfirm([modalBodyText], {
                    title: 'Confirmar',
                    okVariant: 'danger',
                    okTitle: 'Confirmar',
                    cancelTitle: 'Cancelar',
                    centered: true
                })
                if (value) {
                    await this.onConfirmDelete()
                } else {
                    this.$store.commit('RESET_SELECTED_CRUD_OBJECT')
                }
            } catch (error) {
                console.log({ error: error })
                this.unknownErrorToast()
            }
        },
        async onConfirmDelete () {
            var loader = this.$loading.show()
            try {
                const payloadData = { courseSiteId: this.activeSite.id, surveyId: this.selectedCrudObject.id }
                await this.deleteSiteSurvey(payloadData)
                this.$router.replace({ name: 'site-teacher-surveys-list' })
                this.$root.infoToast('La encuesta/quiz ha sido eliminada exitosamente.')
            } catch (error) {
                console.log({ error: error })
                this.$root.dangerToast('No se pudo eliminar la encuesta/quiz, intente más tarde.')
            } finally {
                loader.hide()
                this.$store.commit('RESET_SELECTED_CRUD_OBJECT')
            }
        }
    },
    async created () {
        this.$store.commit('RESET_SELECTED_CRUD_OBJECT')
        await this.loadwholeSurveyData()
    }
}
</script>
